/**
 * The Eisenhower matrix: four boxes made from two yes/no questions.
 *
 * Urgent means it demands attention now (a deadline, a person waiting); important means
 * it moves you toward your own goals. The four answers give the four boxes, with the
 * usual advice for each: do it, schedule it, delegate it, drop it.
 */

export const QUADRANTS = ["do", "schedule", "delegate", "eliminate"] as const;
export type Quadrant = (typeof QUADRANTS)[number];

export const MAX_TASKS = 80;
export const MAX_TASK_LENGTH = 120;

export interface MatrixTask {
  id: string;
  text: string;
  quadrant: Quadrant;
  done: boolean;
}

/** Which box a task belongs in, given the two answers. */
export function quadrantFor(urgent: boolean, important: boolean): Quadrant {
  if (important) {
    return urgent ? "do" : "schedule";
  }
  return urgent ? "delegate" : "eliminate";
}

/** The two answers a box stands for. */
export function answersFor(quadrant: Quadrant): {
  urgent: boolean;
  important: boolean;
} {
  return {
    urgent: quadrant === "do" || quadrant === "delegate",
    important: quadrant === "do" || quadrant === "schedule",
  };
}

const LINE_BREAK = /\r?\n/;
const BULLET = /^\s*(?:[-*•]|\d+[.)])\s+/;

/** One task per non-empty line; bullets and list numbers are dropped. */
export function parseTaskLines(raw: string): string[] {
  return raw
    .split(LINE_BREAK)
    .map((line) => line.replace(BULLET, "").trim())
    .filter((line) => line.length > 0)
    .map((line) => line.slice(0, MAX_TASK_LENGTH));
}

function isQuadrant(value: unknown): value is Quadrant {
  return typeof value === "string" && QUADRANTS.some((key) => key === value);
}

/** Reads saved tasks; anything malformed is skipped. */
export function parseSavedTasks(raw: string | null): MatrixTask[] {
  if (!raw) {
    return [];
  }
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(data)) {
    return [];
  }
  const tasks: MatrixTask[] = [];
  for (const item of data.slice(0, MAX_TASKS)) {
    if (
      typeof item === "object" &&
      item !== null &&
      typeof item.id === "string" &&
      typeof item.text === "string" &&
      isQuadrant(item.quadrant) &&
      !tasks.some((task) => task.id === item.id)
    ) {
      tasks.push({
        id: item.id,
        text: item.text.slice(0, MAX_TASK_LENGTH),
        quadrant: item.quadrant,
        done: item.done === true,
      });
    }
  }
  return tasks;
}

/** A plain-text export grouped by box, in the order of `QUADRANTS`. */
export function matrixText(
  tasks: readonly MatrixTask[],
  labels: Record<Quadrant, string>,
  doneMark: string
): string {
  const blocks: string[] = [];
  for (const quadrant of QUADRANTS) {
    const inBox = tasks.filter((task) => task.quadrant === quadrant);
    const lines = inBox.map(
      (task) => `- ${task.done ? `${doneMark} ` : ""}${task.text}`
    );
    blocks.push([labels[quadrant], ...lines].join("\n"));
  }
  return blocks.join("\n\n");
}

/** Moves a task to another box, leaving every other task alone. */
export function moveTask(
  tasks: readonly MatrixTask[],
  id: string,
  quadrant: Quadrant
): MatrixTask[] {
  return tasks.map((task) => (task.id === id ? { ...task, quadrant } : task));
}
